import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { Alert as MuiAlert, AlertTitle as MuiAlertTitle, Autocomplete, TextField } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import invariant from 'invariant'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useMemo, useRef, useState } from 'react'
import HCaptcha from '@hcaptcha/react-hcaptcha'

import {
  Button,
  Checkbox,
  Container,
  ContentBox,
  Fields,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Icons,
  InputAdornment,
  Radio,
  RadioGroup,
  Recurring,
  Row,
  Scheduled,
  Stack,
  Text,
  Tooltip,
  longWait,
  useAlert,
  validations,
} from '../../components'
import { useLoginDialog } from '../../components/login/useLoginDialog'
import * as SecuritiesPayment from '../../components/payment/securities'
import { mutations, queries } from '../../graphql'
import { useAuth } from '../../hooks'
import palette from '../../palette.json'
import { userName, captureTrackingData } from '../../utils'
import { calculateTipAmount } from './gift-details-math'
import { PaymentMethodsMeta, paymentMethods } from './payment'
import { GuestPaymentMethodsMeta, guestPaymentMethods } from './payment-guest/index'
import { PaymentMethodRadioList } from './PaymentMethodRadioList'
import { onlyShowInDev } from './showUnfinishedFeatures'
import { OTHER_PAYMENT_METHODS, SuccessDialog, getPaymentMethodDisplayName } from './payment/other'

const SKIP_PAYMENT_STEP_METHODS = ['cheque']

const PURPOSE_OF_GIFT_DEFAULTS = ['General Support']

function reorderByDefaults(array, defaults) {
  const pinned = defaults.map((d) => array.find((item) => item.reason === d))
  const rest = array.filter((item) => !defaults.includes(item.reason))
  return pinned.concat(rest)
}

export function GiftAmountStep({
  updateSteps,
  isLoggedIn,
  // charity,
  donationTarget,
  donationTargetId,
  charityOverrides,
  me,
  defaultFund,
  stepperState,
  dispatchStep,
  isDonatingToFund,
}) {
  useEffect(() => {
    // eslint-disable-next-line no-console
    if (onlyShowInDev) console.log(`gift amount stepperState: ${JSON.stringify(stepperState, null, 2)}`)
  }, [stepperState])
  const navigate = useNavigate()
  const location = useLocation()

  // Capture UTM parameters and external referrer (first-touch attribution)
  // If no external referrer, use the charity/fund name as fallback
  const donationTargetName = isDonatingToFund ? donationTarget?.name : donationTarget?.accountName
  const trackingData = captureTrackingData(donationTargetName)

  const captchaRef = useRef()
  const [captchaToken, setCaptchaToken] = useState()
  const { data: { captcha: captchaSiteKey } = {}, refetch: refetchCaptcha } = useQuery(gql`
    query Captcha {
      captcha
    }
  `)

  // A saved draft or a charity default can reference a method that is no longer offered
  const offeredMethodTypes = (isLoggedIn ? paymentMethods : guestPaymentMethods).map((pm) => pm.value)
  const isOfferedMethod = (methodType) => methodType === 'wallet' || offeredMethodTypes.includes(methodType)

  const { register, handleSubmit, formState, getValues, setValue, watch, control } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      paymentMethodType:
        [charityOverrides?.defaultMethod, stepperState?.payment?.paymentMethodType].find(
          (methodType) => methodType && isOfferedMethod(methodType)
        ) ?? (isLoggedIn ? 'wallet' : 'credit-card'),
      amount: stepperState?.giftDetails?.amount ?? '0',
      securities: stepperState?.giftDetails?.securities ?? SecuritiesPayment.defaultSecurities,
      securitiesPath: stepperState?.payment?.selectedPath ?? null,
      addTip: stepperState?.giftDetails?.addTip ?? true,
      customTip: stepperState?.giftDetails?.customTip ?? '',
      isRecurring: stepperState?.giftDetails?.isRecurring ?? false,
      recurringInterval: stepperState?.giftDetails?.recurringInterval || 'monthly',
      purposeOfGrant: stepperState?.giftDetails?.purposeOfGrant ?? '',
      purposeOfGrantNote: stepperState?.giftDetails?.purposeOfGrantNote ?? '',
      processingDate: stepperState?.giftDetails?.processingDate ?? null,
      selfAttestation: stepperState?.giftDetails?.selfAttestation ?? false,
      // charityId: charity?.id,
      donationTarget,
    },
  })

  const { isSubmitting, errors = {} } = formState

  const [{ Alert, alertProps }, { setAlert }] = useAlert()

  const [, { logout }] = useAuth()
  const { loginDialog, startLogin } = useLoginDialog()

  const { profile } = me ?? {}
  const profileName = userName(profile)
  const loggedInDonorFunds = isLoggedIn
    ? (me?.funds ?? []).filter((f) => !f.name?.startsWith('charity-') && !f.closed)
    : []
  const [selectedDonorFundId, setSelectedDonorFundId] = useState(null)

  const selectedFundIdForQuery =
    selectedDonorFundId && selectedDonorFundId !== 'create-new' ? Number(selectedDonorFundId) : null
  const { data: selectedFundData } = useQuery(queries.funds.myFund, {
    skip: !isLoggedIn || !selectedFundIdForQuery,
    variables: { fundId: selectedFundIdForQuery },
  })
  const activeFund = selectedDonorFundId === 'create-new' ? null : selectedFundData?.me?.fund ?? defaultFund

  const [canGiveAsGuest, { data: { canGiveAsGuest: canUseEmail = true } = {} }] = useLazyQuery(
    queries.funds.canGiveAsGuest
  )
  const [getUserByEmail] = useLazyQuery(queries.user.getUserByEmail)

  const [showSuccessDialog, setShowSuccessDialog] = useState(false)

  const [isProcessing, setIsProcessing] = useState(false)

  const checkEmailUse = async () => {
    const email = getValues('email').toLowerCase()

    if (!email) {
      setSelectedDonorFundId(null)
      return true
    }

    const guestResult = await canGiveAsGuest({ variables: { email } })
    const userFromEmail = await getUserByEmail({ variables: { email } })

    // Only confirmed users have the a confirmedAt timestamp.
    const isGuestOrUnconfirmed =
      guestResult?.data?.canGiveAsGuest || typeof userFromEmail?.data?.getUserByEmail?.confirmedAt !== 'string'

    setSelectedDonorFundId(null)

    return isGuestOrUnconfirmed
  }

  const paymentMethodApi = useRef()
  const [_, setPaymentMethodLoading] = useState(false)

  const isAddTipOpen = watch('addTip')
  const purposeOfGrant = watch('purposeOfGrant')
  // const watchProcessingDate = watch('processingDate')
  // const isScheduledGrant = !!watchProcessingDate

  /**
   * initial guest creation functions
   */

  const [guestSignUp] = useMutation(mutations.user.guestSignUp, {
    onCompleted: async () => {},
    onError: async (newErrors) => {
      captchaRef?.current?.resetCaptcha?.()
      // "Email taken" (real, login-capable account) is handled by getOrCreateGuest's
      // catch below, which prompts login instead of showing a generic error
      if (newErrors.message?.includes('Email taken')) return
      setAlert({
        timeout: longWait,
        message: `There was an issue with your email. Please try again.`,
        error: newErrors.message,
        severity: 'error',
      })
    },
  })

  // The backend is the single source of truth for which fund a guest donation
  // should attach to - a client-cached getUserByEmail result can't be trusted
  // (e.g. it can't tell a donor fund apart from a charity's internal fund), so
  // every guest donation round-trips through guestSignUp and lets the backend
  // decide whether to create a new guest, reuse an existing guest's fund, attach
  // a new fund to an unclaimed stub, or reject a real account's email outright.
  const getOrCreateGuest = async (payload) => {
    try {
      const newGuest = await guestSignUp({
        variables: {
          ...payload,
          data: {
            ...payload.data,
            captcha: captchaToken || '',
          },
        },
      })
      return newGuest.data?.guestSignUp
    } catch (err) {
      if (err?.message?.includes('Email taken')) {
        startLogin({
          prefillEmail: payload.data.email,
          notification: (
            <>
              <MuiAlert severity="warning" sx={{ mt: 2 }}>
                <MuiAlertTitle>Welcome back {payload.data.email}</MuiAlertTitle>
                This email already has an account. You&apos;ll need to log in to make a donation.
              </MuiAlert>
              <br />
            </>
          ),
        })
      }
      return null
    }
  }

  let purposeOfGrantNoteLabel = ''
  switch (purposeOfGrant) {
    case 'Fundraising Support':
      purposeOfGrantNoteLabel = 'Name of fundraising project'
      break
    case 'In memory of':
      purposeOfGrantNoteLabel = 'Full Name'
      break
    case 'Other':
      purposeOfGrantNoteLabel = 'Please describe the reason for your gift'
      break
    case 'Thanks for the work you do!':
    default:
      purposeOfGrantNoteLabel = 'Additional note'
      break
  }

  const [designationInput, setDesignationInput] = useState('')

  const purposeOfGiftOptions = useMemo(() => {
    if (donationTarget?.purposeOfGifts?.length) {
      return reorderByDefaults(donationTarget.purposeOfGifts, PURPOSE_OF_GIFT_DEFAULTS)
        .filter(Boolean)
        .filter((p) => p.active && p.reason.trim())
        .map((p) => p.reason)
    }
    return [...PURPOSE_OF_GIFT_DEFAULTS]
  }, [donationTarget?.purposeOfGifts])

  const [requestGivingOptionsInfo] = useMutation(mutations.user.requestGivingOptionsInfo, {
    onCompleted: () => {
      setShowSuccessDialog(true)
    },
    onError: (error) => {
      setAlert({
        timeout: longWait,
        message: `Failed to send giving options request: ${error.message}`,
        severity: 'error',
      })
    },
  })

  const onSubmit = async ({
    email,
    paymentMethodType,
    amount,
    securities,
    addTip,
    customTip,
    purposeOfGrant: grantPurpose,
    purposeOfGrantNote,
    recurringInterval,
    isRecurring,
    processingDate,
    selfAttestation,
    ...payment
  }) => {
    try {
      // const PaymentMethodMeta = PaymentMethodsMeta[paymentMethodType] ?? {}
      const PaymentMethodMeta = !isLoggedIn
        ? GuestPaymentMethodsMeta[paymentMethodType] ?? {}
        : PaymentMethodsMeta[paymentMethodType] ?? {}

      invariant(
        PaymentMethodMeta.getPaymentValues,
        `Payment Method type ${paymentMethodType} is missing getPaymentValues`
      )

      let user
      if (!isLoggedIn) {
        user = await getOrCreateGuest({
          data: {
            firstName: 'Guest',
            middleInitials: '',
            lastName: 'User',
            portalName: '',
            email,
            phone: '',
            address: {
              lineOne: '',
              lineTwo: '',
              city: '',
              province: '',
              postalCode: '',
              country: 'Canada', // You can provide a default value for country if required
            },
            userType: 'guest',
            afterLogin: location.pathname,
            ...trackingData,
          },
        })

        if (!user) {
          captchaRef?.current?.resetCaptcha?.()

          if (!captchaSiteKey) await refetchCaptcha()

          return
        }
        dispatchStep({
          state: {
            fundId: user?.funds[0]?.id,
            user,
          },
        })
      }

      // For 'create-new', the fund is not created yet - that happens on the dedicated
      // New Fund step (NewDonorFundStep) that this step advances to next.
      let activeDonorPortal = null
      if (isLoggedIn && selectedDonorFundId !== 'create-new') {
        activeDonorPortal = loggedInDonorFunds.find((f) => String(f.id) === String(selectedDonorFundId)) ?? null
      }

      setPaymentMethodLoading(true)
      const loginFirst = async (cb) => {
        const canProceedAsGuest = await checkEmailUse()

        if (isLoggedIn || canProceedAsGuest) {
          cb()
        } else {
          startLogin({
            prefillEmail: email,
            onCompleted: () => {
              cb()
              dispatchStep({ state: { isGuest: false, activeDonorPortal: null } })
            },
            notification: (
              <>
                <MuiAlert severity="warning" sx={{ mt: 2 }}>
                  <MuiAlertTitle>Welcome back {email} </MuiAlertTitle>
                  Your donor information is protected by your user account. You&apos;ll need to log in with this email
                  to make a donation.
                </MuiAlert>
                <br />
              </>
            ),
          })
        }
      }

      const updatedStepperState = {
        ...stepperState,
        fundId: user?.funds[0]?.id,
        user,
        giftDetails: {
          ...stepperState.giftDetails,
          isRecurring: PaymentMethodMeta.supportsRecurring === false ? false : isRecurring,
        },
      }
      try {
        let paymentData
        if (paymentMethodType === 'credit-card' || OTHER_PAYMENT_METHODS.includes(paymentMethodType)) {
          paymentData = await PaymentMethodMeta.getPaymentValues({
            paymentMethodApi,
            values: { paymentMethodType, ...payment, stepperState: updatedStepperState, fundId: activeDonorPortal?.id },
          })
        } else if (PaymentMethodMeta.amountType === 'securities') {
          // Include the selected path (advisor vs self-directed) for securities
          paymentData = {
            paymentMethodType,
            selectedPath: getValues('securitiesPath'),
          }
        } else {
          paymentData = { paymentMethodType }
        }

        dispatchStep({
          state: {
            isGuest: !isLoggedIn,
            email,
            fundId: user?.funds[0]?.id,
            user,
            giftDetails: {
              ...(PaymentMethodMeta.amountType === 'securities'
                ? {
                    amountType: 'securities',
                    amount: 0,
                    securities: SecuritiesPayment.defaultSecurities, // Securities entered in step 2
                    addTip: false,
                    customTip: '',
                  }
                : {
                    amountType: PaymentMethodMeta.amountType ?? 'amount',
                    amount,
                    securities: [],
                    addTip,
                    customTip,
                    ...(PaymentMethodMeta.supportsRecurring === false ? { isRecurring: false } : {}),
                  }),
              purposeOfGrant: grantPurpose === '' || grantPurpose == null ? PURPOSE_OF_GIFT_DEFAULTS[0] : grantPurpose,
              purposeOfGrantNote,
              recurringInterval,
              isRecurring: PaymentMethodMeta.supportsRecurring === false ? false : isRecurring,
              processingDate: PaymentMethodMeta.supportsScheduled === false && isLoggedIn ? null : processingDate,
              selfAttestation,
              // charityId: charity.id,
              donationTarget,
              donationTargetId,
              selectedGivingOptions: [paymentMethodType],
            },
            payment: paymentData,
          },
        })
      } finally {
        setPaymentMethodLoading(false)
      }

      if (!isLoggedIn) {
        await loginFirst(() => {
          dispatchStep({ action: 'NEXT' })
        })
      } else {
        dispatchStep({ state: { activeDonorPortal } })
        dispatchStep({ action: 'NEXT' })
      }
    } catch (e) {
      captchaRef?.current?.resetCaptcha?.()
      setAlert({
        timeout: longWait,
        message: 'An error occurred while processing your information. Please try again.',
        error: e.message,
        severity: 'error',
      })
    }
  }

  const paymentMethodType = watch('paymentMethodType')
  const PaymentMethodMeta = !isLoggedIn
    ? GuestPaymentMethodsMeta[paymentMethodType] ?? {}
    : PaymentMethodsMeta[paymentMethodType] ?? {}

  useEffect(() => {
    let paymentMethodCategory

    if (
      paymentMethodType === 'credit-card' ||
      paymentMethodType === 'wallet' ||
      SKIP_PAYMENT_STEP_METHODS.includes(paymentMethodType)
    ) {
      paymentMethodCategory = 'credit'
    } else if (OTHER_PAYMENT_METHODS.includes(paymentMethodType)) {
      paymentMethodCategory = 'other'
    } else {
      paymentMethodCategory = 'not-credit'
    }

    const loginStatus = isLoggedIn ? 'logged-in' : 'not-logged-in'
    const isCreatingNewFund = isLoggedIn && selectedDonorFundId === 'create-new'
    const stepKey = `${loginStatus}-${paymentMethodCategory}${isCreatingNewFund ? '-new-fund' : ''}`

    updateSteps(stepKey)
  }, [paymentMethodType, isLoggedIn, selectedDonorFundId, updateSteps])

  useEffect(() => {
    if (loggedInDonorFunds.length === 1 && !selectedDonorFundId) {
      setSelectedDonorFundId(String(loggedInDonorFunds[0].id))
    }
  }, [loggedInDonorFunds]) // eslint-disable-line react-hooks/exhaustive-deps

  const [tipAmount, setTipAmount] = useState(0)
  useEffect(() => {
    setTipAmount(
      calculateTipAmount({
        amountType: PaymentMethodMeta.amountType ?? 'amount',
        ...getValues(),
      })
    )

    const subscription = watch((values) => {
      setTipAmount(
        calculateTipAmount({
          amountType: PaymentMethodMeta.amountType ?? 'amount',
          ...values,
        })
      )
    })

    return () => subscription.unsubscribe()
  }, [PaymentMethodMeta.amountType, getValues, watch, isLoggedIn])

  return (
    <Container maxWidth="md" sx={{ py: 2 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset disabled={isSubmitting} style={{ display: 'contents', border: 0, p: 0, m: 0 }}>
          <Stack spacing={2}>
            {isLoggedIn ? (
              <>
                {loggedInDonorFunds.length === 0 ? (
                  <MuiAlert severity="warning">
                    <MuiAlertTitle>Welcome back, {profileName}</MuiAlertTitle>
                    This user does not have a Donor Portal. Either{' '}
                    <Link
                      to="/logout"
                      onClick={(e) => {
                        logout({ skipNavigation: true })
                        e.preventDefault()
                        window.location.reload()
                      }}
                    >
                      SIGN OUT
                    </Link>{' '}
                    to use a different profile or{' '}
                    <Link
                      to="."
                      onClick={(e) => {
                        e.preventDefault()
                        setSelectedDonorFundId('create-new')
                        if (getValues('paymentMethodType') === 'wallet') {
                          setValue('paymentMethodType', 'credit-card')
                        }
                      }}
                    >
                      Create Donor Fund
                    </Link>{' '}
                    under this user.
                  </MuiAlert>
                ) : (
                  <MuiAlert severity="info">
                    <MuiAlertTitle>Welcome back, {profileName}</MuiAlertTitle>
                    Select a Giving Fund from the list below and we will auto-fill your information. You will need to{' '}
                    <Link
                      to="/logout"
                      onClick={(e) => {
                        logout({ skipNavigation: true })
                        e.preventDefault()
                        window.location.reload()
                      }}
                    >
                      SIGN OUT
                    </Link>{' '}
                    to complete the transaction under a different user account.
                  </MuiAlert>
                )}

                {loggedInDonorFunds.length >= 1 && (
                  <MuiAlert severity="info" sx={{ mt: 1 }}>
                    <MuiAlertTitle>Select Giving Fund to use for this transaction</MuiAlertTitle>
                    <RadioGroup
                      value={selectedDonorFundId ?? ''}
                      onChange={(e) => {
                        setSelectedDonorFundId(e.target.value)
                        if (e.target.value === 'create-new' && getValues('paymentMethodType') === 'wallet') {
                          setValue('paymentMethodType', 'credit-card')
                        }
                      }}
                      sx={{ mt: 0.5 }}
                    >
                      {loggedInDonorFunds.map((fund) => (
                        <FormControlLabel
                          key={fund.id}
                          value={String(fund.id)}
                          control={<Radio />}
                          label={`${fund.name} (${fund.number})`}
                        />
                      ))}
                      <FormControlLabel value="create-new" control={<Radio />} label="Create a new Giving Fund" />
                    </RadioGroup>
                  </MuiAlert>
                )}
              </>
            ) : (
              <ContentBox border>
                <Stack>
                  <Fields.Text
                    label="Email"
                    required
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    {...register('email', {
                      ...validations.required,
                      onBlur: () => {
                        checkEmailUse()
                      },
                    })}
                  />
                </Stack>
                {!canUseEmail && (
                  <MuiAlert severity="warning" sx={{ mt: 2 }}>
                    <MuiAlertTitle>Welcome back</MuiAlertTitle>
                    You&apos;ll need to{' '}
                    <Link
                      to="/login"
                      state={{ afterLogin: location.pathname }}
                      onClick={(e) => {
                        startLogin({
                          prefillEmail: getValues('email'),
                          onCompleted: () => {
                            // @note the useAuth does share its state so we have to make a fake navigation to re-render with the updated login state
                            const stepKey = `logged-in-${paymentMethodType === 'credit-card' ? 'credit' : 'not-credit'}`
                            updateSteps(stepKey)
                            navigate('.')
                          },
                        })
                        e.preventDefault()
                      }}
                    >
                      log in with this email
                    </Link>{' '}
                    to make a donation.
                  </MuiAlert>
                )}
              </ContentBox>
            )}
            <ContentBox border>
              <Stack spacing={2}>
                <PaymentMethodRadioList
                  control={control}
                  setValue={setValue}
                  getValues={getValues}
                  fund={activeFund}
                  hideRadio={!paymentMethodType}
                  isGuest={!isLoggedIn}
                  required
                  charityOverrides={charityOverrides}
                />
                {paymentMethodType === 'credit-card' &&
                  PaymentMethodMeta.PaymentUI &&
                  (isLoggedIn ? true : canUseEmail) && (
                    <>
                      <Text.H6>Credit Card Details *</Text.H6>
                      <PaymentMethodMeta.PaymentUI
                        ref={paymentMethodApi}
                        fundId={activeFund?.id}
                        {...{ me, defaultFund, register, control, formState, setAlert, setValue, watch }}
                      />
                    </>
                  )}

                {(() => {
                  if (PaymentMethodMeta.amountType === 'securities') {
                    // Step 1: Only show path selection, forms are shown in step 2
                    return (
                      <SecuritiesPayment.SecuritiesPathSelection
                        selectedPath={watch('securitiesPath')}
                        onSelect={(path) => setValue('securitiesPath', path)}
                      />
                    )
                  }
                  if (PaymentMethodMeta.amountType === 'other') {
                    return (
                      <PaymentMethodMeta.PaymentUI
                        ref={paymentMethodApi}
                        paymentType={getPaymentMethodDisplayName(paymentMethodType)}
                        {...{ me, defaultFund, register, control, formState, setAlert }}
                      />
                    )
                  }
                  return (
                    <>
                      <Text.H6>Gift Details *</Text.H6>
                      <Controller
                        control={control}
                        name="amount"
                        rules={{
                          required: 'This field is required',
                          // positive: (val) => parseFloat(val) > 0 || 'Must be a positive number',
                          min: { value: 1, message: 'Must be at least 1' },
                          validate: {
                            sufficientBalance: (value) => {
                              // Only validate wallet balance for wallet payments
                              if (paymentMethodType !== 'wallet') return true

                              const amount = parseFloat(value) || 0
                              const tip = parseFloat(getValues('customTip')) || tipAmount
                              const total = amount + tip
                              const balance = activeFund?.givingWalletBalance || 0

                              if (total > balance) {
                                return `Insufficient funds. Your Giving Wallet balance ($${balance.toFixed(
                                  2
                                )}) is less than the total amount including courtesy payment ($${total.toFixed(2)})`
                              }
                              return true
                            },
                          },
                        }}
                        render={({ field, fieldState }) => (
                          <Fields.Text
                            {...field}
                            label="Amount"
                            error={fieldState.invalid}
                            helperText={fieldState.error?.message}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>,
                              inputComponent: Fields.Amount,
                            }}
                          />
                        )}
                      />
                    </>
                  )
                })()}
                {PaymentMethodMeta.supportsRecurring !== false && (
                  <Recurring register={register} errors={errors} getValues={getValues} setValue={setValue} />
                )}
                {!isDonatingToFund && PaymentMethodMeta.supportsScheduled !== false && isLoggedIn && (
                  <Scheduled
                    register={register}
                    errors={errors}
                    control={control}
                    getValues={getValues}
                    setValue={setValue}
                    entity="Gift"
                  />
                )}
                {!OTHER_PAYMENT_METHODS.includes(paymentMethodType) && (
                  <>
                    {PaymentMethodMeta.amountType !== 'securities' && (
                      <Stack spacing={3}>
                        <Controller
                          control={control}
                          name="addTip"
                          render={({ field }) => (
                            <Row spacing={0}>
                              <FormControlLabel
                                sx={{ mr: 1 }}
                                control={
                                  <Checkbox
                                    {...field}
                                    value="yes"
                                    checked={field.value}
                                    onChange={(e) => {
                                      field.onChange(e.target.checked)
                                    }}
                                  />
                                }
                                label="I want to help cover GiveWise's transaction fees."
                              />
                              <Tooltip title="A courtesy payment helps GiveWise cover platform costs and ongoing improvements so we can continue offering this service free for charities. It does not cover payment provider transaction fees.">
                                <Text.AltBody>
                                  <Icons.HelpOutline sx={{ fontSize: '1rem', cursor: 'pointer' }} />
                                </Text.AltBody>
                              </Tooltip>
                            </Row>
                          )}
                        />
                        {isAddTipOpen && (
                          <Controller
                            control={control}
                            name="customTip"
                            rules={{
                              min: { value: 0, message: 'Must be at least 0' },
                              ...validations.number,
                              validate: {
                                sufficientBalance: (value) => {
                                  // Only validate wallet balance for wallet payments
                                  if (paymentMethodType !== 'wallet') return true

                                  const amount = parseFloat(getValues('amount')) || 0
                                  const tip = parseFloat(value) || tipAmount
                                  const total = amount + tip
                                  const balance = activeFund?.givingWalletBalance || 0

                                  if (total > balance) {
                                    return `Insufficient funds. Your Giving Wallet balance ($${balance.toFixed(
                                      2
                                    )}) is less than the total amount including courtesy payment ($${total.toFixed(2)})`
                                  }
                                  return true
                                },
                              },
                            }}
                            render={({ field, fieldState }) => (
                              <Fields.Text
                                {...field}
                                label="Courtesy Payment"
                                error={fieldState.invalid}
                                helperText={fieldState.error?.message}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                }}
                                value={field.value || tipAmount.toFixed(2)}
                                onChange={(e) => {
                                  field.onChange(e.target.value)
                                }}
                              />
                            )}
                          />
                        )}
                      </Stack>
                    )}
                    <div
                      style={{
                        height: '1px',
                        width: '100%',
                        backgroundColor: 'darkgrey',
                        marginTop: '24px',
                      }}
                    />
                    {!isDonatingToFund && (
                      <>
                        <Text.Body>Purpose of Gift</Text.Body>
                        <Stack>
                          <Autocomplete
                            fullWidth
                            options={purposeOfGiftOptions}
                            value={purposeOfGrant || null}
                            inputValue={designationInput}
                            onOpen={() => setDesignationInput('')}
                            onInputChange={(_event, newValue) => setDesignationInput(newValue)}
                            onChange={(_event, newValue) => {
                              setValue('purposeOfGrant', newValue ?? '', { shouldValidate: true })
                            }}
                            disableClearable
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Please select the gift designation"
                                error={!!errors?.purposeOfGrant}
                                helperText={errors?.purposeOfGrant?.message}
                              />
                            )}
                          />

                          {purposeOfGrant && purposeOfGrant !== 'Thanks for the work you do!' && (
                            <Fields.Text
                              label={purposeOfGrantNoteLabel}
                              name="purposeOfGrantNote"
                              error={!!errors?.purposeOfGrantNote}
                              helperText={errors?.purposeOfGrantNote?.message}
                              {...register('purposeOfGrantNote')}
                            />
                          )}
                        </Stack>
                      </>
                    )}
                    <Text.Bold>Self-attestation *</Text.Bold>
                    <FormControl error={!!errors?.selfAttestation}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            sx={{
                              color: errors?.selfAttestation ? palette.red : 'black',
                            }}
                            {...register('selfAttestation', { ...validations.required })}
                            checked={getValues('selfAttestation')}
                            onChange={(e) => {
                              setValue('selfAttestation', e.target.checked, { shouldValidate: true })
                            }}
                          />
                        }
                        label="I certify that my gift is for a permitted purpose
							  and will not result in a more than incidental benefit to me or persons related to me."
                      />
                      {errors?.selfAttestation && <FormHelperText>{errors?.selfAttestation?.message}</FormHelperText>}
                    </FormControl>
                  </>
                )}
                {!isLoggedIn && captchaSiteKey && (
                  <HCaptcha sitekey={captchaSiteKey} onVerify={setCaptchaToken} ref={captchaRef} />
                )}

                <Alert {...alertProps} sx={{ alignSelf: 'flex-end' }} />
              </Stack>
            </ContentBox>

            <Row spacing={2} justifyContent="flex-end">
              {OTHER_PAYMENT_METHODS.includes(paymentMethodType) && isLoggedIn ? (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || isProcessing}
                  onClick={async () => {
                    try {
                      setIsProcessing(true)

                      if (isLoggedIn) {
                        const currentPaymentMethod = getValues('paymentMethodType')
                        if (selectedDonorFundId === 'create-new') {
                          // Fund doesn't exist yet - collect its details on the dedicated
                          // New Fund step, which submits the info request once created.
                          dispatchStep({
                            action: 'NEXT',
                            state: { payment: { paymentMethodType: currentPaymentMethod } },
                          })
                        } else {
                          await requestGivingOptionsInfo({
                            variables: {
                              options: [currentPaymentMethod],
                              fundId: me?.funds?.[0]?.id,
                              email: me?.email,
                              charityName: donationTarget.accountName,
                              charityNumber: donationTarget.id,
                              isDonatingToFund,
                              donationTargetName: isDonatingToFund ? donationTarget.name : donationTarget.accountName,
                              donationTargetId: donationTarget.id,
                            },
                          })
                        }
                      } else {
                        setShowSuccessDialog(true)
                      }
                    } catch (error) {
                      setAlert({
                        timeout: longWait,
                        message: 'An error occurred while processing your request. Please try again. ',
                        error: error.message,
                        severity: 'error',
                      })
                    } finally {
                      setIsProcessing(false)
                    }
                  }}
                >
                  {isProcessing ? 'Processing...' : 'Submit & Connect'}
                </Button>
              ) : (
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={
                    isSubmitting ||
                    (isLoggedIn && !selectedDonorFundId) ||
                    (isLoggedIn ? false : !canUseEmail) ||
                    (PaymentMethodMeta.amountType === 'securities' && !watch('securitiesPath'))
                  }
                >
                  Continue
                </Button>
              )}
            </Row>
          </Stack>
        </fieldset>
      </form>

      {loginDialog}

      {showSuccessDialog && (
        <SuccessDialog
          open
          onClose={() => setShowSuccessDialog(false)}
          isLoggedIn={isLoggedIn}
          fundId={me?.funds?.[0]?.id}
          selectedOption={getValues('paymentMethodType')}
          userEmail={me?.email}
          isDonatingToFund={isDonatingToFund}
          donationTargetName={isDonatingToFund ? donationTarget?.name : donationTarget?.accountName}
          donationTargetId={donationTargetId}
        />
      )}
    </Container>
  )
}
